import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Banknote, Clock, Briefcase, ChevronDown, ChevronUp, Loader2, Info, User, Shield, Wrench, Car, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSiteContent } from '../hooks/useSiteContent';

import { BACKEND_URL } from '../config/api';
import { todayYMD } from '../utils/dateHelpers';
const MIN_DAY_RATE = 120;

const PPE_OPTIONS = [
  { id: 'BOOTS', label: 'Safety Boots' },
  { id: 'HI_VIS', label: 'Hi-Vis' },
  { id: 'HARD_HAT', label: 'Hard Hat' },
  { id: 'GLOVES', label: 'Gloves' },
  { id: 'EYE', label: 'Eye Protection' },
  { id: 'EAR', label: 'Ear Protection' }
];

// SectionHeader component moved outside to avoid re-creation on each render
const SectionHeader = ({ title, icon: Icon, section, expanded, onToggle, required = false }) => (
  <button
    type="button"
    onClick={() => onToggle(section)}
    className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
  >
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 text-orange-500" />
      <span className="font-medium text-slate-900">{title}</span>
      {required && <span className="text-red-500 text-sm">*</span>}
    </div>
    {expanded ? (
      <ChevronUp className="h-5 w-5 text-slate-400" />
    ) : (
      <ChevronDown className="h-5 w-5 text-slate-400" />
    )}
  </button>
);

export default function OfferForm({
  onSubmit,
  onCancel,
  initialData = null,
  isCounter = false,
  counterRole = 'employer', // 'tradesperson' shows the locked-down counter UI
  loading = false
}) {
  const isTradeCounter = isCounter && counterRole === 'tradesperson';
  const { get } = useSiteContent();
  const [templates, setTemplates] = useState({ global_templates: [], user_templates: [] });
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    dates: true,
    location: true,
    pay: true,
    extras: false
  });

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/offer-templates`, {
          withCredentials: true
        });
        setTemplates(response.data);
      } catch (error) {
        console.warn('OfferForm: failed to fetch templates', error.message);
      } finally {
        setLoadingTemplates(false);
      }
    };
    if (!isCounter) {
      fetchTemplates();
    } else {
      setLoadingTemplates(false);
    }
  }, [isCounter]);

  const [formData, setFormData] = useState({
    // Dates
    startDate: initialData?.startDate || '',
    endDate: initialData?.endDate || '',
    singleDay: initialData?.singleDay ?? true,
    hoursStart: initialData?.hoursStart || '08:00',
    hoursEnd: initialData?.hoursEnd || '16:30',
    breaks: initialData?.breaks || 'UNPAID',
    
    // Location
    locationPostcode: initialData?.locationPostcode || '',
    locationAddressLine1: initialData?.locationAddressLine1 || '',
    locationAddressLine2: initialData?.locationAddressLine2 || '',
    locationTown: initialData?.locationTown || '',
    locationCounty: initialData?.locationCounty || '',
    
    // Pay
    rateType: initialData?.rateType || 'DAY',
    dayRateGBP: initialData?.dayRateGBP || '',
    
    // Details
    tasksNote: initialData?.tasksNote || '',
    experienceLevel: initialData?.experienceLevel || '',
    crewSize: initialData?.crewSize || 1,
    
    // Counter note (required for counter offers)
    counterNote: '',
    
    // Extras
    extras: {
      cisAvailable: initialData?.extras?.cisAvailable || false,
      utrRequired: initialData?.extras?.utrRequired || false,
      invoiceRequired: initialData?.extras?.invoiceRequired || false,
      paymentTerms: initialData?.extras?.paymentTerms || 'WEEKLY',
      overtime: initialData?.extras?.overtime || 'NONE',
      inductionRequired: initialData?.extras?.inductionRequired || false,
      securityCheck: initialData?.extras?.securityCheck || false,
      cscsRequired: initialData?.extras?.cscsRequired || false,
      cscsLevel: initialData?.extras?.cscsLevel || null,
      tools: initialData?.extras?.tools || null,
      ppeRequired: initialData?.extras?.ppeRequired || [],
      materialsProvided: initialData?.extras?.materialsProvided || false,
      consumablesProvided: initialData?.extras?.consumablesProvided || false,
      parkingAvailable: initialData?.extras?.parkingAvailable || false,
      welfareOnSite: initialData?.extras?.welfareOnSite || false,
      smokingArea: initialData?.extras?.smokingArea || false,
      indoorWork: initialData?.extras?.indoorWork || false,
      noiseRestrictions: initialData?.extras?.noiseRestrictions || false
    }
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleExtrasChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      extras: { ...prev.extras, [field]: value }
    }));
  };

  const applyTemplate = (template) => {
    if (!template?.template_data) return;
    
    const data = template.template_data;
    setFormData(prev => ({
      ...prev,
      // Keep dates as they need to be set fresh
      hoursStart: data.hoursStart || prev.hoursStart,
      hoursEnd: data.hoursEnd || prev.hoursEnd,
      breaks: data.breaks || prev.breaks,
      locationPostcode: data.locationPostcode || prev.locationPostcode,
      locationTown: data.locationTown || prev.locationTown,
      rateType: data.rateType || prev.rateType,
      dayRateGBP: data.dayRateGBP || prev.dayRateGBP,
      tasksNote: data.tasksNote || prev.tasksNote,
      experienceLevel: data.experienceLevel || prev.experienceLevel,
      extras: {
        ...prev.extras,
        ...(data.extras || {})
      }
    }));
    toast.success(`Applied template: ${template.name}`);
  };

  const allTemplates = [...(templates.global_templates || []), ...(templates.user_templates || [])];

  const handlePPEToggle = (ppeId) => {
    setFormData(prev => {
      const current = prev.extras.ppeRequired || [];
      const updated = current.includes(ppeId)
        ? current.filter(id => id !== ppeId)
        : [...current, ppeId];
      return {
        ...prev,
        extras: { ...prev.extras, ppeRequired: updated }
      };
    });
  };

  const validateForm = () => {
    if (!formData.startDate) {
      toast.error('Start date is required');
      return false;
    }
    if (!formData.singleDay && !formData.endDate) {
      toast.error('End date is required for multi-day jobs');
      return false;
    }
    if (!formData.locationPostcode) {
      toast.error('Postcode is required');
      return false;
    }
    if (!formData.locationAddressLine1) {
      toast.error('Address line 1 is required');
      return false;
    }
    if (formData.rateType === 'DAY') {
      if (!formData.dayRateGBP || parseFloat(formData.dayRateGBP) < MIN_DAY_RATE) {
        toast.error(`Day rate must be at least £${MIN_DAY_RATE}`);
        return false;
      }
    }
    if (isCounter) {
      if (!formData.counterNote || formData.counterNote.length < 20) {
        toast.error('Counter note must be at least 20 characters');
        return false;
      }
      if (formData.counterNote.length > 200) {
        toast.error('Counter note must be 200 characters or less');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    const submitData = {
      ...formData,
      dayRateGBP: formData.rateType === 'DAY' ? parseFloat(formData.dayRateGBP) : null,
      crewSize: parseInt(formData.crewSize) || 1
    };
    
    onSubmit(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="offer-form">
      {/* Template Selector (not for counter offers) */}
      {!isCounter && allTemplates.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-orange-500" />
            <span className="text-sm font-medium text-slate-700">Use a Template</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {allTemplates.map(tpl => (
              <button
                key={tpl.template_id}
                type="button"
                onClick={() => applyTemplate(tpl)}
                className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:border-orange-300 hover:bg-orange-50 transition-colors"
                data-testid={`template-btn-${tpl.template_id}`}
              >
                {tpl.name}
                {tpl.is_global && <span className="ml-1 text-orange-500">★</span>}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">Click a template to pre-fill common values</p>
        </div>
      )}

      {/* Counter Note (if counter offer) */}
      {isCounter && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-amber-800 mb-2">
            Counter Note (required) *
          </label>
          <textarea
            value={formData.counterNote}
            onChange={(e) => handleChange('counterNote', e.target.value)}
            placeholder="Explain your counter offer (20-200 characters)..."
            rows={3}
            maxLength={200}
            className="w-full text-sm"
            data-testid="counter-note-input"
          />
          <p className="text-xs text-amber-600 mt-1">{formData.counterNote.length}/200 characters</p>
        </div>
      )}

      {/* Dates Section */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <SectionHeader title="When do you need them?" icon={Calendar} section="dates" expanded={expandedSections.dates} onToggle={toggleSection} required />
        {expandedSections.dates && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  min={todayYMD()}
                  className="w-full"
                  required
                  data-testid="start-date-input"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.singleDay}
                    onChange={(e) => handleChange('singleDay', e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">One day only</span>
                </label>
              </div>
            </div>
            
            {!formData.singleDay && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  min={formData.startDate || todayYMD()}
                  className="w-full"
                  required={!formData.singleDay}
                  data-testid="end-date-input"
                />
                <p className="text-xs text-slate-500 mt-1">Last day of the job</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Time</label>
                <input
                  type="time"
                  value={formData.hoursStart}
                  onChange={(e) => handleChange('hoursStart', e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Time</label>
                <input
                  type="time"
                  value={formData.hoursEnd}
                  onChange={(e) => handleChange('hoursEnd', e.target.value)}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Breaks</label>
              <select
                value={formData.breaks}
                onChange={(e) => handleChange('breaks', e.target.value)}
                className="w-full"
              >
                <option value="PAID">Paid breaks</option>
                <option value="UNPAID">Unpaid breaks</option>
                <option value="NA">Not applicable</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Location Section — locked for tradesperson counters (read-only,
          summarised below). The tradesperson can only renegotiate dates +
          rate; address/scope stays as the employer set it. */}
      {!isTradeCounter && (
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <SectionHeader title="Job Location" icon={MapPin} section="location" expanded={expandedSections.location} onToggle={toggleSection} required />
        {expandedSections.location && (
          <div className="p-4 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">{get('offer.address_note', 'Full address shared only after they accept')}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Postcode *</label>
              <input
                type="text"
                value={formData.locationPostcode}
                onChange={(e) => handleChange('locationPostcode', e.target.value.toUpperCase())}
                placeholder="SW1A 1AA"
                className="w-full"
                required
                data-testid="postcode-input"
              />
              <p className="text-xs text-slate-500 mt-1">{get('offer.postcode_helper', 'Where the work will be done')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1 *</label>
              <input
                type="text"
                value={formData.locationAddressLine1}
                onChange={(e) => handleChange('locationAddressLine1', e.target.value)}
                placeholder="123 High Street"
                className="w-full"
                required
                data-testid="address-line1-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
              <input
                type="text"
                value={formData.locationAddressLine2}
                onChange={(e) => handleChange('locationAddressLine2', e.target.value)}
                placeholder="Unit 4, 2nd floor (optional)"
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Town/City</label>
                <input
                  type="text"
                  value={formData.locationTown}
                  onChange={(e) => handleChange('locationTown', e.target.value)}
                  placeholder="Town or city"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">County</label>
                <input
                  type="text"
                  value={formData.locationCounty}
                  onChange={(e) => handleChange('locationCounty', e.target.value)}
                  placeholder="County"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Read-only summary of locked job context shown to the
          tradesperson during counter so they know what they're countering. */}
      {isTradeCounter && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 text-sm" data-testid="counter-locked-context">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Job from employer (locked)</p>
          <p className="text-slate-700"><strong>Address:</strong> {[formData.locationAddressLine1, formData.locationTown, formData.locationCounty, formData.locationPostcode].filter(Boolean).join(', ') || '—'}</p>
          {formData.tasksNote && <p className="text-slate-700 whitespace-pre-wrap"><strong>Scope:</strong> {formData.tasksNote}</p>}
          <p className="text-xs text-slate-500">You can only counter the dates and the day rate. The employer set the address and scope.</p>
        </div>
      )}

      {/* Pay Section */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <SectionHeader title="Day Rate" icon={Banknote} section="pay" expanded={expandedSections.pay} onToggle={toggleSection} required />
        {expandedSections.pay && (
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rateType"
                  value="DAY"
                  checked={formData.rateType === 'DAY'}
                  onChange={() => handleChange('rateType', 'DAY')}
                  className="text-orange-500"
                />
                <span className="text-sm text-slate-700">I'll set the rate</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="rateType"
                  value="OPEN_TO_OFFERS"
                  checked={formData.rateType === 'OPEN_TO_OFFERS'}
                  onChange={() => handleChange('rateType', 'OPEN_TO_OFFERS')}
                  className="text-orange-500"
                />
                <span className="text-sm text-slate-700">Let them quote</span>
              </label>
            </div>

            {formData.rateType === 'DAY' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Day Rate (£) *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                  <input
                    type="number"
                    value={formData.dayRateGBP}
                    onChange={(e) => handleChange('dayRateGBP', e.target.value)}
                    placeholder={`${MIN_DAY_RATE}`}
                    min={MIN_DAY_RATE}
                    step="5"
                    className="w-full pl-8"
                    required={formData.rateType === 'DAY'}
                    data-testid="day-rate-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {get('offer.rate_helper', `Min £${MIN_DAY_RATE}. Check their profile rate for guidance.`).replace('{min_rate}', MIN_DAY_RATE)}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment Terms</label>
              <select
                value={formData.extras.paymentTerms}
                onChange={(e) => handleExtrasChange('paymentTerms', e.target.value)}
                className="w-full"
              >
                <option value="SAME_DAY">Same Day</option>
                <option value="WEEKLY">Weekly</option>
                <option value="14_DAYS">14 Days</option>
                <option value="30_DAYS">30 Days</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Extras Section (Collapsible) — hidden during a tradesperson counter
          since they only renegotiate dates/rate. */}
      {!isTradeCounter && (
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <SectionHeader title="Job Details (Optional)" icon={Briefcase} section="extras" expanded={expandedSections.extras} onToggle={toggleSection} />
        {expandedSections.extras && (
          <div className="p-4 space-y-6">
            {/* Tasks Note */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">What's the work?</label>
              <textarea
                value={formData.tasksNote}
                onChange={(e) => handleChange('tasksNote', e.target.value.slice(0, 200))}
                placeholder="E.g., First fix electrics for 3-bed extension, rewire kitchen..."
                rows={3}
                maxLength={200}
                className="w-full text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">
                {formData.tasksNote.length}/200 – {get('offer.tasks_helper', 'A clear brief gets better responses')}
              </p>
            </div>

            {/* Experience Level */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Experience Level</label>
              <select
                value={formData.experienceLevel}
                onChange={(e) => handleChange('experienceLevel', e.target.value)}
                className="w-full"
              >
                <option value="">Not specified</option>
                <option value="IMPROVER">Improver</option>
                <option value="SKILLED">Skilled Tradesperson</option>
                <option value="SUPERVISOR">Supervisor/Lead</option>
              </select>
            </div>

            {/* Crew Size */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Crew Size</label>
              <input
                type="number"
                value={formData.crewSize}
                onChange={(e) => handleChange('crewSize', e.target.value)}
                min={1}
                max={10}
                className="w-full"
              />
            </div>

            {/* CSCS */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.extras.cscsRequired}
                  onChange={(e) => handleExtrasChange('cscsRequired', e.target.checked)}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700">CSCS Card Required</span>
              </label>
              {formData.extras.cscsRequired && (
                <select
                  value={formData.extras.cscsLevel || ''}
                  onChange={(e) => handleExtrasChange('cscsLevel', e.target.value || null)}
                  className="w-full text-sm"
                >
                  <option value="">Any level</option>
                  <option value="GREEN">Green (Labourer)</option>
                  <option value="BLUE">Blue (Skilled)</option>
                  <option value="GOLD">Gold (Supervisor)</option>
                  <option value="BLACK">Black (Manager)</option>
                </select>
              )}
            </div>

            {/* Tools */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tools</label>
              <select
                value={formData.extras.tools || ''}
                onChange={(e) => handleExtrasChange('tools', e.target.value || null)}
                className="w-full"
              >
                <option value="">Not specified</option>
                <option value="OWN">Bring own tools</option>
                <option value="PROVIDED">Tools provided</option>
                <option value="MIX">Mix of both</option>
              </select>
            </div>

            {/* PPE */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">PPE Required</label>
              <div className="flex flex-wrap gap-2">
                {PPE_OPTIONS.map(ppe => (
                  <button
                    key={ppe.id}
                    type="button"
                    onClick={() => handlePPEToggle(ppe.id)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      formData.extras.ppeRequired.includes(ppe.id)
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {ppe.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Checkboxes */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'cisAvailable', label: 'CIS Available' },
                { key: 'inductionRequired', label: 'Induction Required' },
                { key: 'parkingAvailable', label: 'Parking Available' },
                { key: 'welfareOnSite', label: 'Welfare On Site' },
                { key: 'materialsProvided', label: 'Materials Provided' },
                { key: 'indoorWork', label: 'Indoor Work' }
              ].map(item => (
                <label key={item.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.extras[item.key]}
                    onChange={(e) => handleExtrasChange(item.key, e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Save as Template Option (for employers only, not counter offers) */}
      {!isCounter && (
        <div className="border-t border-slate-200 pt-4">
          <button
            type="button"
            onClick={async () => {
              const name = prompt('Enter a name for this template:');
              if (!name || !name.trim()) return;
              
              try {
                await axios.post(
                  `${BACKEND_URL}/api/offer-templates`,
                  {
                    template: {
                      name: name.trim(),
                      locationPostcode: formData.locationPostcode,
                      locationTown: formData.locationTown,
                      rateType: formData.rateType,
                      dayRateGBP: formData.dayRateGBP ? parseFloat(formData.dayRateGBP) : null,
                      hoursStart: formData.hoursStart,
                      hoursEnd: formData.hoursEnd,
                      breaks: formData.breaks,
                      experienceLevel: formData.experienceLevel,
                      tasksNote: formData.tasksNote,
                      extras: formData.extras
                    },
                    is_global: false
                  },
                  { withCredentials: true }
                );
                toast.success('Template saved! You can use it for future offers.');
              } catch (error) {
                toast.error('Failed to save template');
              }
            }}
            className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
            data-testid="save-template-btn"
          >
            <FileText className="h-4 w-4" />
            Save current settings as a template
          </button>
        </div>
      )}

      {/* Submit Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary flex-1"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary flex-1 flex items-center justify-center gap-2"
          disabled={loading}
          data-testid="send-offer-btn"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <Briefcase className="h-4 w-4" />
              {isCounter ? 'Send Counter' : 'Send Offer'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
